import type { ReactElement, ReactNode } from 'react';

import styles from './Banner.module.css';
import type { Hue } from './hue';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import type { IconName } from './iconName';

/**
 * A persistent inline notice — docs/DESIGN.md §6.5, ADR-0101 §7.8.
 *
 * The soft-limit warning and the auto-repair report are both this component. It is NOT a
 * toast: it stays until the condition clears or the user dismisses it, and it displaces the
 * document instead of covering it.
 *
 * `role="status"` (polite), never `alert`: the banner is already on screen and stays there, so
 * an assertive interruption would be announcing something the user can read at their leisure.
 *
 * ═══ SEVERITY STILL TRAVELS ON THREE CHANNELS ═══
 *
 * Glyph shape, the machine label WORD, and the tone — so `label` is still required. What
 * ADR-0101 §7.8 changes is where the word is DRAWN: it moved out of the headline row and into
 * a visually hidden prefix inside the message. The tile beside the sentence already says
 * "something was repaired" in shape and colour at a glance, and a mono-caps "AUTO-REPAIRED"
 * over the top of it spent the widest column in the row repeating it. The word is still in
 * `textContent`, still first in reading order, and still what a screen reader hears before the
 * sentence; only the pixels are gone.
 *
 * ═══ TONE IS A FAMILY, NOT A COLOUR ═══
 *
 * Each tone names one of the seven hues of ADR-0101 §1 and the stylesheet keys `[data-hue]`
 * onto that family, exactly as `Button` does. `'repair'` is the new tone and the reason the
 * mapping exists: the auto-repair report used to borrow `warning`, which made it amber — the
 * hue this system reserves for "changed but still valid". A repair is lemon, and lemon means
 * "the tool rewrote this for you", which is precisely the claim the banner makes.
 */

export type BannerTone = 'success' | 'info' | 'warning' | 'danger' | 'repair';

const GLYPH: Record<BannerTone, IconName> = {
  success: 'check',
  info: 'info',
  warning: 'warning',
  danger: 'error',
  repair: 'spark',
};

/** Tone → hue family (ADR-0101 §1). The stylesheet keys `[data-hue]`, never the tone. */
const HUE: Record<BannerTone, Hue> = {
  success: 'lime',
  info: 'aqua',
  warning: 'amber',
  danger: 'coral',
  repair: 'lemon',
};

export interface BannerProps {
  readonly tone: BannerTone;
  /**
   * The machine label, e.g. `Auto-repaired`. Localised by the caller; rendered as a visually
   * hidden prefix of the message, so it is heard and greppable but no longer drawn.
   */
  readonly label: string;
  /** The headline. Named inline values may be wrapped in `<b>` by the caller. */
  readonly children: ReactNode;
  /**
   * The second sentence — the one that qualifies the headline ("Everything the safe rules
   * could repair has been repaired."). It is always in `textContent`, and it is DRAWN only
   * from 960px, where there is a row wide enough to hold it beside the chips without either
   * of them shrinking the other. Below that it is visually hidden rather than removed: a
   * screen-reader user's window is not 960px wide.
   */
  readonly note?: ReactNode;
  /**
   * Optional chip row — the repair chips of docs/DESIGN.md §6.5 (`trailing commas ×3`),
   * already localised. Scrolls horizontally under a fade rather than wrapping.
   *
   * STRINGS, not `ReactNode`: a chip's look is this layer's business and the caller's business
   * is the text. `src/app/` builds them from `Notice.repair.chips`, and if it were handed the
   * markup it would need this module's CSS class — which is how presentation leaks upwards
   * into a composition root one `className` at a time.
   */
  readonly detail?: readonly string[];
  /** Right-hand controls, e.g. an `Undo` button. A peer of the message, never hidden. */
  readonly actions?: ReactNode;
  /** Renders the × control. Both halves or neither — a nameless close button is not offered. */
  readonly dismiss?: { readonly label: string; readonly onDismiss: () => void };
  readonly className?: string;
  readonly id?: string;
}

export function Banner({
  tone,
  label,
  children,
  note,
  detail,
  actions,
  dismiss,
  className,
  id,
}: BannerProps): ReactElement {
  const classes = [styles.banner, className].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      id={id}
      // Explicit border under Windows High Contrast, where the tone step collapses (base.css).
      data-surface="banner"
      // The hue drives every colour below; the tone is kept because it is the SEVERITY, and a
      // stylesheet or a user script that wants "the dangerous one" must not have to know that
      // danger happens to be coral this year.
      data-hue={HUE[tone]}
      data-tone={tone}
      role="status"
    >
      <span aria-hidden="true" className={styles.tile}>
        <Icon name={GLYPH[tone]} size={16} />
      </span>

      <p className={styles.title}>
        {/* Channel two of the severity: the WORD. `::after` adds the colon, so the element's
            own text stays exactly the label a test (and a translator) wrote. */}
        <span className={styles.machineLabel}>{label}</span>
        {children}
        {note === undefined ? null : <small className={styles.note}>{note}</small>}
      </p>

      {detail === undefined || detail.length === 0 ? null : (
        <div className={styles.detail}>
          {detail.map((text) => (
            <span className={styles.chip} key={text}>
              {text}
            </span>
          ))}
        </div>
      )}

      {actions === undefined ? null : <div className={styles.actions}>{actions}</div>}

      {dismiss === undefined ? null : (
        // Bare and `sm`: the banner already carries a tinted action (Undo), and ADR-0101 §1
        // rule 2 allows exactly one emphasised control per surface. The touch floor is kept by
        // `--ctl-sm`, which is 36px below 960 and 32 above it, where a pointer is present.
        <IconButton
          className={styles.dismiss}
          icon="close"
          label={dismiss.label}
          onClick={dismiss.onDismiss}
          size="sm"
        />
      )}
    </div>
  );
}

export default Banner;
